import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Input from "../../shared/Field/input";
import DatePicker from "../../shared/Field/date";
import Select from "../../shared/Field/select";
import PrimaryButton from "../../shared/buttons/primaryButton";
import Progress from "../../shared/progress";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useAuth } from "../../../hooks/useAuth";
import {
  useUpdateProfile,
  useDeleteEmployee,
  useRestoreEmployee,
  usePermanentlyDeleteEmployee,
  useGetPositions,
  useGetDepartments,
} from "../../../api/hooks";
import { loginSuccess } from "../../../store/slice/authSlice";
import { toast } from "react-hot-toast";
import ProbationTrack from "../../employee/ProbationTrack";
import ExtendProbationModal from "../../employee/ExtendProbationModal";
import EndProbationModal from "../../employee/EndProbationModal";
import {
  DEFAULT_PROBATION_MONTHS,
  getProbationTrack,
} from "../../../utils/leaveEntitlement";

const getDepartmentId = (department) => {
  if (!department) return "";
  if (typeof department === "object") return String(department._id || "");
  return String(department);
};

const UserProfile = ({ user, disableEdit, canDelete, employeeId }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExtendProbation, setShowExtendProbation] = useState(false);
  const [showEndProbation, setShowEndProbation] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(user?.profileImage || "");
  const [imgError, setImgError] = useState(false);
  const fileInputRef = useRef(null);
  const {
    user: currentUser,
    companyId: currentCompanyId,
    isProfileComplete: currentProfileComplete,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Check if we're on the employee details page and user is admin or has delete permission
  const isEmployeePage = location.pathname.includes("/employees/");
  const isAdmin = currentUser?.role === "company-admin";
  const isOwnProfile =
    currentUser?._id === employeeId || currentUser?._id === user?._id;
  const isEmployeeOwnProfile =
    isOwnProfile && currentUser?.role === "employee";
  const canEditPositionDepartment = !isEmployeeOwnProfile;
  const showDeleteButton = isEmployeePage && (isAdmin || canDelete) && !isOwnProfile;

  const companyId = currentCompanyId || currentUser?.company;

  const { data: positionsData, isLoading: positionsLoading } =
    useGetPositions(companyId);
  const { data: departments } = useGetDepartments(companyId);

  const positionOptions = React.useMemo(() => {
    const names = (positionsData?.positions || [])
      .filter((pos) => pos.isActive)
      .map((position) => position.name);

    if (user?.position && !names.includes(user.position)) {
      names.unshift(user.position);
    }

    return names;
  }, [positionsData, user?.position]);

  const departmentOptions = React.useMemo(() => {
    const currentDepartmentId = getDepartmentId(user?.department);
    const list = (departments || [])
      .filter(
        (dept) => dept.isActive || String(dept._id) === currentDepartmentId
      )
      .map((dept) => ({ value: String(dept._id), label: dept.name }));

    if (
      currentDepartmentId &&
      user?.department?.name &&
      !list.some((option) => option.value === currentDepartmentId)
    ) {
      list.unshift({
        value: currentDepartmentId,
        label: user.department.name,
      });
    }

    return list;
  }, [departments, user?.department]);

  // Determine which employeeId to use for updates
  const targetEmployeeId = isOwnProfile ? null : employeeId;

  // Delete employee mutation
  const archiveEmployeeMutation = useDeleteEmployee();
  const restoreEmployeeMutation = useRestoreEmployee();
  const permanentDeleteMutation = usePermanentlyDeleteEmployee();
  const isArchived = Boolean(user?.isArchived);
  const deleteEmployeeMutation = isArchived
    ? permanentDeleteMutation
    : archiveEmployeeMutation;

  // Initial form values from the user object
  const initialValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    position: user?.position || "",
    department: getDepartmentId(user?.department),
    location: user?.location || "",
    birthday: user?.dob || null,
    email: user?.email || "",
    mobile: user?.phoneNumber || "",
    skype: user?.skype || "",
    isOnProbation: Boolean(user?.isOnProbation),
    joiningDate: user?.joiningDate || user?.createdAt || null,
    probationPeriodMonths:
      user?.probationPeriodMonths || DEFAULT_PROBATION_MONTHS,
  };

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email format"),
    mobile: Yup.string().matches(
      /^[+]?[\d\s\-()]+$/,
      "Invalid phone number format"
    ),
    joiningDate: Yup.mixed().nullable(),
    probationPeriodMonths: Yup.number().min(1).max(36),
  });

  // Handle successful profile update
  const handleUpdateSuccess = (updatedData) => {
    setIsEditMode(false);
    toast.success("Profile updated successfully!");

    // If this is the current user's profile, update Redux state
    if (isOwnProfile && updatedData?.employee) {
      const mergedUser = {
        ...currentUser,
        ...updatedData.employee,
        positionDetails:
          updatedData.employee.positionDetails ??
          currentUser?.positionDetails ??
          null,
      };

      dispatch(
        loginSuccess({
          user: mergedUser,
          companyId:
            updatedData.employee.company ??
            currentCompanyId ??
            mergedUser?.company ??
            null,
          isProfileComplete:
            updatedData.employee.isProfileComplete ??
            currentProfileComplete ??
            true,
        })
      );
    }

    setSelectedImageFile(null);
    setPreviewImage(updatedData?.employee?.profileImage || "");
  };

  // Handle successful employee deletion
  const handleDeleteSuccess = () => {
    toast.success(
      isArchived
        ? "Employee deleted permanently"
        : "Employee moved to Former Employees"
    );
    navigate("/employees"); // Redirect to employees list
  };

  const handleRestoreEmployee = async () => {
    try {
      await restoreEmployeeMutation.mutateAsync(employeeId || user?._id);
      toast.success("Employee restored successfully!");
    } catch (error) {
      toast.error("Failed to restore employee. Please try again.");
      console.error("Restore employee error:", error);
    }
  };

  // Update profile mutation - pass employeeId if updating another employee
  const updateProfileMutation = useUpdateProfile(
    handleUpdateSuccess,
    targetEmployeeId
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true, // This will reinitialize the form when initialValues change
    onSubmit: async (values) => {
      try {
        const normalizedData = {
          firstName: values.firstName,
          lastName: values.lastName,
          location: values.location,
          dob: values.birthday,
          phoneNumber: values.mobile,
          skype: values.skype,
          email: values.email,
        };

        if (canEditPositionDepartment) {
          normalizedData.position = values.position;
          normalizedData.department = values.department || null;
          normalizedData.isOnProbation = Boolean(values.isOnProbation);
          normalizedData.joiningDate = values.joiningDate || null;
          if (values.isOnProbation) {
            normalizedData.probationPeriodMonths =
              Number(values.probationPeriodMonths) || DEFAULT_PROBATION_MONTHS;
          }
        }

        if (
          canEditPositionDepartment &&
          normalizedData.isOnProbation &&
          !normalizedData.joiningDate &&
          !user?.createdAt
        ) {
          toast.error("Joining date or employee created date is required");
          return;
        }

        let updatePayload = normalizedData;

        if (selectedImageFile) {
          updatePayload = new FormData();
          Object.entries(normalizedData).forEach(([key, value]) => {
            if (key === "department") {
              updatePayload.append(key, value || "");
              return;
            }
            if (key === "isOnProbation") {
              updatePayload.append(key, value ? "true" : "false");
              return;
            }
            if (key === "joiningDate") {
              updatePayload.append(key, value || "");
              return;
            }
            if (key === "probationPeriodMonths") {
              updatePayload.append(key, String(value || DEFAULT_PROBATION_MONTHS));
              return;
            }
            if (value !== undefined && value !== null && value !== "") {
              updatePayload.append(key, value);
            }
          });
          updatePayload.append("profileImage", selectedImageFile);
        }

        await updateProfileMutation.mutateAsync(updatePayload);
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Failed to update profile. Please try again."
        );
        console.error("Profile update error:", error);
      }
    },
  });

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      resetForm({
        values: {
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          position: user?.position || "",
          department: getDepartmentId(user?.department),
          location: user?.location || "",
          birthday: user?.dob || null,
          email: user?.email || "",
          mobile: user?.phoneNumber || "",
          skype: user?.skype || "",
          isOnProbation: Boolean(user?.isOnProbation),
          joiningDate: user?.joiningDate || user?.createdAt || null,
          probationPeriodMonths:
            user?.probationPeriodMonths || DEFAULT_PROBATION_MONTHS,
        },
      });

      if (!selectedImageFile) {
        setPreviewImage(user?.profileImage || "");
        setImgError(false);
      }
    }
  }, [user, resetForm, selectedImageFile]);

  useEffect(() => {
    if (!isEditMode) {
      setSelectedImageFile(null);
      return;
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!selectedImageFile) {
      setPreviewImage(user?.profileImage || "");
      setImgError(false);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedImageFile);
    setPreviewImage(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedImageFile, user?.profileImage]);

  // Handle date change
  const handleDateChange = (e) => {
    setFieldValue("birthday", e.target.value);
  };

  const handleImageInputClick = () => {
    if (!isEditMode || disableEdit) return;
    fileInputRef.current?.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      event.target.value = "";
      return;
    }

    setSelectedImageFile(file);
    event.target.value = "";
  };

  // Handle delete employee
  const handleDeleteEmployee = async () => {
    try {
      await deleteEmployeeMutation.mutateAsync(employeeId || user?._id);
      handleDeleteSuccess();
    } catch (error) {
      toast.error("Failed to remove employee. Please try again.");
      console.error("Delete employee error:", error);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <div
      className="w-full lg:w-[280px] shrink-0 bg-white lg:overflow-hidden
       pb-4 h-fit lg:h-full text-[#0A1629]
rounded-3xl flex flex-col"
    >
      <div className="flex flex-col border-b border-[#E4E6E8] p-5">
        <div className="flex justify-between">
          <div className="relative group">
            <Progress size={54} currentValue={user?.progressValue ?? 0} />
            <div className="absolute top-0 left-0 right-0 scale-85 bottom-0 w-full h-full rounded-full overflow-hidden flex items-center justify-center bg-black text-white text-xl font-bold">
              {previewImage && !imgError ? (
                <img
                  src={previewImage}
                  alt={`${user?.firstName || "User"} profile`}
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                user?.firstName?.charAt(0).toUpperCase() || "U"
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
            {isEditMode && !disableEdit && (
              <button
                type="button"
                onClick={handleImageInputClick}
                className="absolute top-0 left-0 right-0 bottom-0 rounded-full bg-black/50 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center px-2 text-center"
              >
                Change Photo
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <PrimaryButton
              disable={disableEdit}
              onclick={() => setIsEditMode(!isEditMode)}
              icon={"/icons/edit.svg"}
              className={`${isEditMode ? `bg-[#3F8CFF]` : `bg-[#F4F9FD]`}`}
            />
            {showDeleteButton && isArchived && (
              <button
                onClick={handleRestoreEmployee}
                disabled={restoreEmployeeMutation.isLoading}
                className="h-10 px-3 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-medium shadow-sm disabled:opacity-50"
                title="Restore Employee"
              >
                {restoreEmployeeMutation.isLoading ? "Restoring..." : "Restore"}
              </button>
            )}
            {showDeleteButton && (
              <button
                onClick={() =>
                  !deleteEmployeeMutation.isLoading &&
                  setShowDeleteConfirm(true)
                }
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${deleteEmployeeMutation.isLoading
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 hover:scale-105 active:scale-95"
                  } shadow-sm`}
                disabled={deleteEmployeeMutation.isLoading}
                title={isArchived ? "Delete Permanently" : "Remove Employee"}
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        <h4 className="text-lg font-medium mt-2">
          {[user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()}
        </h4>
        <span className="text-xs text-gray-600  capitalize">
          {user?.position}
        </span>
        {(values.isOnProbation || user?.isOnProbation) && (
          <span className="mt-2 inline-flex w-fit items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
            On probation
          </span>
        )}
        {!values.isOnProbation &&
          !user?.isOnProbation &&
          getProbationTrack(user)?.isCompleted && (
            <span className="mt-2 inline-flex w-fit items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
              Probation completed
            </span>
          )}
        {selectedImageFile && isEditMode && (
          <span className="text-[11px] text-[#3F8CFF] mt-1">
            New profile photo selected
          </span>
        )}
      </div>
      <form
        onSubmit={handleSubmit}
        className="flex h-full flex-col px-5 py-4 md:overflow-y-auto scrollbar-hide"
      >
        <div className="flex flex-col gap-y-3">
          <h4 className=" font-medium">Main info</h4>

          <Input
            errors={errors}
            touched={touched}
            onchange={handleChange}
            name={"firstName"}
            value={values}
            readOnly={!isEditMode}
            title="First Name"
            placeholder="John"
          />
          <Input
            errors={errors}
            touched={touched}
            onchange={handleChange}
            name={"lastName"}
            value={values}
            readOnly={!isEditMode}
            title="Last Name"
            placeholder="Doe"
          />
          {isEditMode && canEditPositionDepartment ? (
            <Select
              errors={errors}
              touched={touched}
              name="position"
              value={values.position || ""}
              onChange={handleChange}
              title="Position"
              placeholder="Select Position"
              options={positionOptions}
              disabled={positionsLoading || !positionOptions.length}
            />
          ) : (
            <Input
              readOnly
              name="position"
              value={values}
              title="Position"
              placeholder="—"
            />
          )}

          {isEditMode && canEditPositionDepartment ? (
            <Select
              errors={errors}
              touched={touched}
              name="department"
              value={values.department || ""}
              onChange={handleChange}
              title="Department"
              placeholder="Select Department"
              options={departmentOptions}
              disabled={!departmentOptions.length}
            />
          ) : (
            <Input
              readOnly
              name="departmentDisplay"
              value={{
                departmentDisplay:
                  departmentOptions.find(
                    (option) => option.value === values.department
                  )?.label ||
                  user?.department?.name ||
                  "",
              }}
              title="Department"
              placeholder="Not assigned"
            />
          )}
          <Input
            errors={errors}
            touched={touched}
            onchange={handleChange}
            name={"location"}
            value={values}
            readOnly={!isEditMode}
            title="Location"
            placeholder="NYC, New York, USA"
          />
          <DatePicker
            errors={errors}
            touched={touched}
            onChange={handleDateChange}
            name={"birthday"}
            value={values.birthday}
            readOnly={!isEditMode}
            title="Birthday Date"
          />
        </div>
        <div className="flex flex-col gap-y-3 mt-7">
          <h4 className=" font-medium">Employment</h4>
          {isEditMode && canEditPositionDepartment ? (
            <label className="flex items-center justify-between rounded-[14px] border-2 border-[#D8E0F0]/80 px-4 py-3 cursor-pointer">
              <div className="pr-3">
                <p className="text-sm font-semibold text-[#0A1629]">
                  On probation
                </p>
                <p className="text-[11px] text-gray-400 font-medium">
                  Paid leave is locked. Unpaid leave is allowed.
                </p>
              </div>
              <input
                type="checkbox"
                name="isOnProbation"
                checked={Boolean(values.isOnProbation)}
                onChange={(event) =>
                  setFieldValue("isOnProbation", event.target.checked)
                }
                className="h-4 w-4 accent-[#3F8CFF]"
              />
            </label>
          ) : (
            <Input
              readOnly
              name="probationDisplay"
              value={{
                probationDisplay: values.isOnProbation ? "Yes" : "No",
              }}
              title="On probation"
              placeholder="No"
            />
          )}
          <DatePicker
            errors={errors}
            touched={touched}
            onChange={(e) => setFieldValue("joiningDate", e.target.value)}
            name="joiningDate"
            value={values.joiningDate}
            readOnly={!isEditMode || !canEditPositionDepartment}
            title="Joining Date"
          />
          {(isEditMode && canEditPositionDepartment
            ? values.isOnProbation
            : Boolean(user?.isOnProbation) || values.isOnProbation) && (
            <>
              {isEditMode && canEditPositionDepartment && (
                <Input
                  errors={errors}
                  touched={touched}
                  onchange={handleChange}
                  name="probationPeriodMonths"
                  value={values}
                  type="number"
                  title="Probation period (months)"
                  placeholder="3"
                />
              )}
              <ProbationTrack
                employee={user}
                employeeId={employeeId || user?._id}
                compact
                canEdit={canEditPositionDepartment && !isEmployeeOwnProfile}
              />
              {canEditPositionDepartment && !isEmployeeOwnProfile && (
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEndProbation(true)}
                    className="w-full py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
                  >
                    End probation
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowExtendProbation(true)}
                    className="w-full py-2 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold hover:bg-amber-100 transition-colors"
                  >
                    Extend probation
                  </button>
                </div>
              )}
            </>
          )}
          {!(values.isOnProbation || user?.isOnProbation) &&
            getProbationTrack(user)?.hasProbationHistory && (
              <ProbationTrack
                employee={user}
                employeeId={employeeId || user?._id}
                compact
                canEdit={canEditPositionDepartment && !isEmployeeOwnProfile}
              />
            )}
        </div>
        <div className="flex flex-col gap-y-3 mt-7">
          <h4 className=" font-medium">Contact Info</h4>
          <Input
            errors={errors}
            touched={touched}
            onchange={handleChange}
            name={"email"}
            value={values}
            readOnly={!isEditMode}
            title="Email"
            placeholder="evanyates@gmail.com"
          />
          <Input
            errors={errors}
            touched={touched}
            onchange={handleChange}
            name={"mobile"}
            value={values}
            readOnly={!isEditMode}
            title="Mobile Number"
            placeholder="+1 675 346 23-10"
          />
          <Input
            errors={errors}
            touched={touched}
            onchange={handleChange}
            name={"skype"}
            value={values}
            readOnly={!isEditMode}
            title="Skype"
            placeholder="Evan 2256"
          />
        </div>

        {/* Save Button - Only show when in edit mode */}
        {isEditMode && (
          <div className="mt-6 pt-4 border-t border-[#E4E6E8]">
            <PrimaryButton
              type="submit"
              title={
                updateProfileMutation.isLoading ? "Saving..." : "Save Changes"
              }
              className="w-full bg-[#3F8CFF] text-white py-2 px-4 rounded-lg"
              disable={updateProfileMutation.isLoading}
            />
          </div>
        )}
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isArchived ? "Delete Permanently" : "Remove Employee"}
            </h3>
            {isArchived ? (
              <>
                <p className="text-gray-600 mb-6">
                  This will permanently delete this former employee and:
                </p>
                <ul className="text-sm text-gray-600 mb-6 list-disc list-inside">
                  <li>Delete their leave, attendance and notifications</li>
                  <li>Unassign their tasks</li>
                  <li>Delete their account</li>
                </ul>
                <p className="text-red-600 text-sm font-medium mb-6">
                  This action cannot be undone. Use Restore instead if they may return.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  This employee will be moved to Former Employees:
                </p>
                <ul className="text-sm text-gray-600 mb-6 list-disc list-inside">
                  <li>They can no longer log in</li>
                  <li>They are removed from all projects</li>
                  <li>Their tasks, leave and performance history are kept</li>
                </ul>
                <p className="text-gray-600 text-sm mb-6">
                  You can restore them anytime from Employees → Former.
                </p>
              </>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={deleteEmployeeMutation.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEmployee}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                disabled={deleteEmployeeMutation.isLoading}
              >
                {deleteEmployeeMutation.isLoading
                  ? "Working..."
                  : isArchived
                    ? "Delete Permanently"
                    : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showExtendProbation && (
        <ExtendProbationModal
          employee={user}
          employeeId={employeeId || user?._id}
          onClose={() => setShowExtendProbation(false)}
        />
      )}
      {showEndProbation && (
        <EndProbationModal
          employee={user}
          employeeId={employeeId || user?._id}
          onClose={() => setShowEndProbation(false)}
        />
      )}
    </div>
  );
};

export default UserProfile;
